/**
 * `defer-screenshot-redaction` — admin-config schema and curated
 * default presets for the no-code Conversational Debug + run-history
 * screenshot redaction pipeline.
 *
 * Operators set the policy ("blur every password input, blackout
 * elements tagged `[data-sensitive]`, scrub credit-card-looking
 * strings out of console logs"); the runner enforces it just before
 * `page.screenshot()` and just before persisting console logs to the
 * run record. v1 ships:
 *
 *   * `selectors` — CSS selectors to redact (block-list of element
 *     classes / attributes / inputs that the runner overlays before
 *     capture). Free-form so operators can lock down their own
 *     `.pii-name` / `[data-payment]` patterns.
 *   * `useDefaultSelectors` — toggle the curated preset list
 *     ("passwords", "credit-card inputs", "email inputs") on/off.
 *   * `redactConsoleLogs` — apply PII regexes to console log text
 *     before it's written into the run's step results. This is the
 *     "I don't want our customer's email to live in our run history"
 *     toggle.
 *   * `redactionStyle` — `'blur'` (default, visually informative —
 *     reviewers can tell where the field WAS) vs `'blackout'` (full
 *     opaque rectangle — strongest privacy stance).
 *
 * v2 deferred:
 *   * OCR-based redaction (run Tesseract over the screenshot after
 *     capture to mask text matching a regex). Heavier and rarely
 *     adds privacy beyond what `selectors` + `blur` already give.
 *   * Per-flow overrides (this is admin-level v1).
 *   * Region-painter UI on `<VisualBaselineReview>` (the painter that
 *     shipped for `pending-applitools-visual-ai` v1.1 is the right
 *     surface; today operators add selectors as text in admin).
 */

import { z } from 'zod';

export const ScreenshotRedactionStyleSchema = z.enum(['blur', 'blackout']);
export type ScreenshotRedactionStyle = z.infer<typeof ScreenshotRedactionStyleSchema>;

/**
 * Curated CSS selector presets. Each preset is a named bundle of
 * selectors that's safe to apply across most web apps. Operators
 * tick them on/off and add their own bespoke selectors next to them.
 */
export const SCREENSHOT_REDACTION_DEFAULT_PRESETS: Array<{
  id: string;
  label: string;
  description: string;
  selectors: string[];
}> = [
  {
    id: 'passwords',
    label: 'Password inputs',
    description: 'Any `<input type="password">` is masked before capture.',
    selectors: ['input[type="password"]'],
  },
  {
    id: 'credit-cards',
    label: 'Payment / card-number inputs',
    description: 'Input controls commonly used for card numbers, CVV, expiry.',
    selectors: [
      'input[autocomplete="cc-number"]',
      'input[autocomplete="cc-csc"]',
      'input[autocomplete="cc-exp"]',
      'input[autocomplete="cc-name"]',
      'input[name*="card" i]',
      'input[id*="card" i]',
    ],
  },
  {
    id: 'auth-tokens',
    label: 'Auth / token inputs',
    description: 'API key, OTP, and 2-FA inputs.',
    selectors: [
      'input[autocomplete="one-time-code"]',
      'input[name*="otp" i]',
      'input[name*="apikey" i]',
      'input[name*="token" i]',
    ],
  },
  {
    id: 'data-pii',
    label: 'Elements tagged `[data-pii]`',
    description: 'Convention for "I am declaring this element private". Pair with `data-pii` in your own DOM.',
    selectors: ['[data-pii]', '[data-sensitive]', '[data-redact]'],
  },
];

/**
 * Curated console-log redaction regex bundle. Always applied as a
 * post-processing pass on captured console output when
 * `redactConsoleLogs` is true. The bundle is hard-coded (not
 * operator-tunable in v1) so the privacy guarantee is consistent
 * across orgs; operator-supplied regexes are deferred to v2.
 *
 * Each entry has:
 *   * `id` — stable identifier for telemetry / audit logs.
 *   * `pattern` — JS regex source string (compiled with the `g`
 *     flag inside the runner so callers don't have to re-parse).
 *   * `replacement` — what to substitute for the match.
 */
export const SCREENSHOT_REDACTION_CONSOLE_PATTERNS: Array<{ id: string; pattern: string; replacement: string }> = [
  {
    id: 'email',
    // Email regex — intentionally not the full RFC 5322 monster.
    // The conservative shape catches > 99% of real addresses and
    // produces zero false positives on common log noise.
    pattern: '\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}\\b',
    replacement: '[redacted-email]',
  },
  {
    id: 'credit-card',
    // 13-16 digits, optionally with single dashes or spaces between
    // four-digit groups. Tightened with word boundaries so we don't
    // eat long numeric ids.
    pattern: '\\b(?:\\d[ -]?){13,16}\\b',
    replacement: '[redacted-card]',
  },
  {
    id: 'jwt',
    // Three base64url segments separated by dots — JWT canonical form.
    pattern: '\\beyJ[A-Za-z0-9_-]{10,}\\.[A-Za-z0-9_-]{10,}\\.[A-Za-z0-9_-]{10,}\\b',
    replacement: '[redacted-jwt]',
  },
  {
    id: 'phone-e164',
    // E.164 phone numbers (+ then 8-15 digits).
    pattern: '\\+\\d{8,15}\\b',
    replacement: '[redacted-phone]',
  },
  {
    id: 'ssn-us',
    // US SSNs (NNN-NN-NNNN). Conservative — only the dashed form.
    pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b',
    replacement: '[redacted-ssn]',
  },
];

// ----------------------------------------------------------------------------
// Pure runtime helpers (used by the mvp runner + the api redactor).
//
// Lives in `@rynora/contracts` rather than `@rynora/api` because the
// mvp package can only depend on `@rynora/contracts` — it must not
// reach into backend code.
// ----------------------------------------------------------------------------

/** Characters that would let an operator break out of the injected stylesheet. */
const CSS_BLOCK_CHARS = /[{};<>`\n\r]/g;
/** Tokens that would let an operator pull in remote rules. */
const CSS_FORBIDDEN_TOKENS = /(@import|@charset|@namespace|expression\s*\()/gi;

/**
 * Strip characters that could close the surrounding `{}` block or
 * smuggle in a remote import. Operators supply free-form selectors
 * in admin-config and we treat them as untrusted input.
 *
 * Exported for direct unit testing.
 */
export function sanitiseCssSelector(selector: string): string {
  const noBlock = selector.replace(CSS_BLOCK_CHARS, ' ');
  return noBlock.replace(CSS_FORBIDDEN_TOKENS, ' ').trim();
}

function styleToCssBody(style: 'blur' | 'blackout'): string {
  if (style === 'blackout') {
    return [
      'background-color: #000 !important',
      'color: transparent !important',
      'text-shadow: none !important',
      'filter: none !important',
      'opacity: 1 !important',
    ].join('; ');
  }
  return [
    'filter: blur(8px) !important',
    '-webkit-filter: blur(8px) !important',
    'background-clip: padding-box !important',
  ].join('; ');
}

/**
 * Build the redaction stylesheet for one config. Returns an empty
 * string when `enabled` is false OR no selectors apply — so the
 * runner can unconditionally call this and `addStyleTag` only if the
 * result is non-empty.
 */
export function buildRedactionCss(config: ScreenshotRedactionConfig): string {
  if (!config.enabled) return '';
  const selectors: string[] = [];
  if (config.useDefaultSelectors) {
    for (const preset of SCREENSHOT_REDACTION_DEFAULT_PRESETS) {
      for (const selector of preset.selectors) {
        const safe = sanitiseCssSelector(selector);
        if (safe) selectors.push(safe);
      }
    }
  }
  for (const operatorSelector of config.selectors) {
    const safe = sanitiseCssSelector(operatorSelector);
    if (safe) selectors.push(safe);
  }
  if (selectors.length === 0) return '';

  const body = styleToCssBody(config.redactionStyle);
  const grouped = Array.from(new Set(selectors)).join(',\n  ');
  return `/* rynora-screenshot-redaction: applied before capture */\n${grouped} {\n  ${body};\n}\n`;
}

/**
 * Compile the curated console-redaction regex bundle into a single
 * `(text) => text` function. Cheap to call; safe to call from hot
 * paths because the regexes are compiled once at module-load time.
 */
export function compileConsoleRedactor(config: ScreenshotRedactionConfig): (text: string) => string {
  if (!config.enabled || !config.redactConsoleLogs) {
    return (text) => text;
  }
  const compiled = SCREENSHOT_REDACTION_CONSOLE_PATTERNS.map((p) => ({
    re: new RegExp(p.pattern, 'g'),
    replacement: p.replacement,
  }));
  return (text: string): string => {
    if (typeof text !== 'string' || text.length === 0) return text;
    let out = text;
    for (const { re, replacement } of compiled) {
      out = out.replace(re, replacement);
    }
    return out;
  };
}

/**
 * Apply the compiled redactor to every string value inside a console
 * log line shape. Used by the runner when it ships console output
 * into the run record.
 */
export function redactConsoleLine<T extends { args?: unknown[]; text?: string }>(
  line: T,
  redact: (text: string) => string,
): T {
  if (!line) return line;
  const out: { args?: unknown[]; text?: string } = { ...line };
  if (typeof line.text === 'string') {
    out.text = redact(line.text);
  }
  if (Array.isArray(line.args)) {
    out.args = line.args.map((arg) => (typeof arg === 'string' ? redact(arg) : arg));
  }
  return out as T;
}

export const ScreenshotRedactionConfigSchema = z.object({
  /** Master kill switch. Off by default to mirror visualAi / monitors. */
  enabled: z.boolean().default(false),
  /** Apply the curated default preset list (`SCREENSHOT_REDACTION_DEFAULT_PRESETS`). */
  useDefaultSelectors: z.boolean().default(true),
  /**
   * Operator-supplied selectors merged on top of the default
   * presets. Free-form CSS selectors. The runner sanitises out any
   * `}`, `;`, `<`, `>`, newline, backtick, or `@import` token before
   * injecting the stylesheet to keep the CSS context closed.
   */
  selectors: z.array(z.string().min(1).max(500)).default([]),
  /** How the redacted area looks. `blur` is the default. */
  redactionStyle: ScreenshotRedactionStyleSchema.default('blur'),
  /** Apply console-log regex redactions before they land in the run record. */
  redactConsoleLogs: z.boolean().default(true),
});
export type ScreenshotRedactionConfig = z.infer<typeof ScreenshotRedactionConfigSchema>;
